import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Play, Pause, Loader2, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import StatusPill from "@/components/ui/StatusPill";
import { ArtThumb } from "@/components/riptag/TrackTable";
import { TrackDetailsPanel } from "./TrackDetailsPanel";

const DECADES = ["1960s", "1970s", "1980s", "1990s", "2000s", "2010s", "2020s", "Unclassified"];
const STATUS_PILL = { active: "published", inactive: "draft", review: "pending" };

export const MusicLibraryTable = ({ preset, onChanged }) => {
  const [q, setQ] = useState("");
  const [f, setF] = useState({ genre: "", artist: "", decade: "", status: "" });
  const [sort, setSort] = useState({ key: "added", dir: "desc" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ tracks: [], total: 0, page_size: 25 });
  const [meta, setMeta] = useState({ artists: [], decades: [] });
  const [genres, setGenres] = useState([]);
  const [stations, setStations] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [detailId, setDetailId] = useState("");
  const [bulk, setBulk] = useState({ field: "lib_status", value: "active" });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [preview, setPreview] = useState({ id: "", state: "idle" });
  const audioRef = useRef(null);
  const urlCache = useRef({});

  const reqId = useRef(0);
  const load = useCallback(async (filters, query, srt, pg) => {
    const rid = ++reqId.current;
    try {
      const r = await api.get("/music/tracks", {
        params: { q: query, ...filters, station: filters.station || "", sort: srt.key, dir: srt.dir, page: pg, page_size: 25 } });
      if (rid !== reqId.current) return;
      setData(r.data);
      setSelected(new Set());
    } catch { /* station not ready */ }
  }, []);

  useEffect(() => {
    api.get("/music/filters").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/music/genres").then((r) => setGenres(r.data.genres || [])).catch(() => {});
    api.get("/music/stations").then((r) => setStations(r.data.stations || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const p = preset || {};
    const nf = { genre: p.genre || "", artist: "", decade: p.decade || "", status: "", station: p.station || "" };
    setF(nf);
    setPage(1);
    load(nf, "", sort, 1);
    api.get("/music/filters").then((r) => setMeta(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  useEffect(() => () => { audioRef.current?.pause(); Object.values(urlCache.current).forEach((u) => URL.revokeObjectURL(u)); }, []);

  const refresh = (pg = page) => load(f, q, sort, pg);
  const changeFilter = (k, v) => { const nf = { ...f, [k]: v }; setF(nf); setPage(1); load(nf, q, sort, 1); };
  const doSort = (key) => {
    const dir = sort.key === key && sort.dir === "asc" ? "desc" : "asc";
    setSort({ key, dir });
    load(f, q, { key, dir }, page);
  };

  const togglePlay = async (t, e) => {
    e.stopPropagation();
    const audio = audioRef.current || (audioRef.current = new Audio());
    if (preview.id === t.track_id && preview.state === "playing") { audio.pause(); setPreview({ id: t.track_id, state: "paused" }); return; }
    if (preview.id === t.track_id && preview.state === "paused") { audio.play(); setPreview({ id: t.track_id, state: "playing" }); return; }
    audio.pause();
    setPreview({ id: t.track_id, state: "loading" });
    try {
      let url = urlCache.current[t.track_id];
      if (!url) {
        const r = await api.get(`/music/tracks/${t.track_id}/audio`, { responseType: "blob" });
        url = URL.createObjectURL(r.data);
        urlCache.current[t.track_id] = url;
      }
      audio.src = url;
      audio.onended = () => setPreview({ id: "", state: "idle" });
      await audio.play();
      setPreview({ id: t.track_id, state: "playing" });
    } catch { toast.error("Couldn't load audio"); setPreview({ id: "", state: "idle" }); }
  };

  const toggleSel = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((p) => (p.size === data.tracks.length ? new Set() : new Set(data.tracks.map((t) => t.track_id))));

  const analyzeSelected = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/music/analyze", { track_ids: [...selected] });
      toast.success(`Analyzing ${r.data.total} tracks — free local analysis. Follow progress in the Audio Analysis tab.`);
      setSelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start analysis"); }
    setBulkBusy(false);
  };

  const applyBulk = async () => {
    setBulkBusy(true);
    try {
      const body = { track_ids: [...selected] };
      if (bulk.field === "add_station" || bulk.field === "remove_station") body[bulk.field] = bulk.value;
      else body[bulk.field] = bulk.value;
      const r = await api.post("/music/tracks/bulk", body);
      toast.success(`${r.data.affected} track${r.data.affected === 1 ? "" : "s"} updated`);
      refresh(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk edit failed"); }
    setBulkBusy(false);
  };

  const bulkOptions = {
    lib_status: ["active", "inactive", "review"],
    rotation: ["unassigned", "heavy", "medium", "light"],
    decade: DECADES,
    genre: genres.map((g) => g.name),
    add_station: stations.map((s) => s.station_id),
    remove_station: stations.map((s) => s.station_id),
  };
  const stationName = (id) => stations.find((s) => s.station_id === id)?.name || id;
  const pages = Math.max(1, Math.ceil(data.total / (data.page_size || 25)));

  const SortTh = ({ k, children, cls = "" }) => (
    <th className={`px-2 py-2 text-left cursor-pointer select-none ${cls}`} onClick={() => doSort(k)}>
      <span className="inline-flex items-center gap-0.5">{children}
        {sort.key === k && (sort.dir === "asc" ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
      </span>
    </th>
  );

  return (
    <div className={`grid grid-cols-1 ${detailId ? "xl:grid-cols-12" : ""} gap-4`} data-testid="ml-library">
      <div className={detailId ? "xl:col-span-8 min-w-0" : "min-w-0"}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-48">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(f, q, sort, 1); } }}
              placeholder="Search title, artist, album… (Enter)"
              className="w-full rounded-md border border-line bg-bg-surface pl-8 pr-2 py-2 text-xs focus:border-brand-amber focus:outline-none"
              data-testid="ml-search-input" />
          </div>
          <select value={f.genre} onChange={(e) => changeFilter("genre", e.target.value)} data-testid="ml-filter-genre"
            className="rounded-md border border-line bg-bg-surface px-2 py-2 text-xs">
            <option value="">All Genres</option>
            {genres.filter((g) => g.count > 0).map((g) => <option key={g.name} value={g.name}>{g.name} ({g.count})</option>)}
          </select>
          <select value={f.artist} onChange={(e) => changeFilter("artist", e.target.value)} data-testid="ml-filter-artist"
            className="rounded-md border border-line bg-bg-surface px-2 py-2 text-xs max-w-40">
            <option value="">All Artists</option>
            {meta.artists.map((a) => <option key={a.artist} value={a.artist}>{a.artist} ({a.n})</option>)}
          </select>
          <select value={f.decade} onChange={(e) => changeFilter("decade", e.target.value)} data-testid="ml-filter-decade"
            className="rounded-md border border-line bg-bg-surface px-2 py-2 text-xs">
            <option value="">All Decades</option>
            {DECADES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={f.status} onChange={(e) => changeFilter("status", e.target.value)} data-testid="ml-filter-status"
            className="rounded-md border border-line bg-bg-surface px-2 py-2 text-xs">
            <option value="">Any Status</option>
            {["active", "inactive", "review"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {f.station && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/50 bg-sky-500/10 px-2.5 py-1 text-[11px] text-sky-300"
              data-testid="ml-station-chip">
              Station: {stationName(f.station)}
              <button onClick={() => changeFilter("station", "")} className="hover:text-white" data-testid="ml-station-chip-clear">×</button>
            </span>
          )}
        </div>

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 mb-3"
            data-testid="ml-bulk-bar">
            <span className="type-button-label text-brand-amber">{selected.size} selected</span>
            <select value={bulk.field}
              onChange={(e) => setBulk({ field: e.target.value, value: bulkOptions[e.target.value][0] || "" })}
              className="rounded-md border border-line bg-bg-surface px-2 py-1.5 text-xs" data-testid="ml-bulk-field">
              <option value="lib_status">Set status</option>
              <option value="rotation">Set rotation</option>
              <option value="decade">Set decade</option>
              <option value="genre">Set genre</option>
              <option value="add_station">Assign to station</option>
              <option value="remove_station">Unassign station</option>
            </select>
            <select value={bulk.value} onChange={(e) => setBulk((p) => ({ ...p, value: e.target.value }))}
              className="rounded-md border border-line bg-bg-surface px-2 py-1.5 text-xs max-w-44" data-testid="ml-bulk-value">
              {(bulkOptions[bulk.field] || []).map((v) => (
                <option key={v} value={v}>{bulk.field.includes("station") ? stationName(v) : v}</option>
              ))}
            </select>
            <button onClick={applyBulk} disabled={bulkBusy || !bulk.value} data-testid="ml-bulk-apply"
              className="type-button-label rounded-md bg-brand-amber px-3 py-1.5 text-bg-base disabled:opacity-50">
              {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : "Apply"}
            </button>
            <button onClick={analyzeSelected} disabled={bulkBusy} data-testid="ml-bulk-analyze"
              className="type-button-label rounded-md border border-cyan-500/50 px-3 py-1.5 text-brand-cyan hover:bg-cyan-500/10 disabled:opacity-50">
              Analyze audio
            </button>
          </div>
        )}

        <div className="rounded-xl border border-line overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-bg-surface text-ink-muted font-mono uppercase text-[10px]">
              <tr>
                <th className="px-2 py-2 w-8">
                  <input type="checkbox" checked={data.tracks.length > 0 && selected.size === data.tracks.length}
                    onChange={toggleAll} className="accent-amber-500" data-testid="ml-select-all" />
                </th>
                <th className="px-2 py-2 w-10"></th>
                <SortTh k="title">Title</SortTh>
                <SortTh k="artist">Artist</SortTh>
                <SortTh k="album" cls="hidden lg:table-cell">Album</SortTh>
                <SortTh k="year">Year</SortTh>
                <th className="px-2 py-2 text-left">Genre</th>
                <SortTh k="bpm" cls="hidden lg:table-cell">BPM</SortTh>
                <th className="px-2 py-2 text-left hidden lg:table-cell">Energy</th>
                <th className="px-2 py-2 text-left">Stations</th>
                <th className="px-2 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.tracks.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-ink-muted" data-testid="ml-table-empty">
                  No tracks match — import music or clear filters.
                </td></tr>
              )}
              {data.tracks.map((t) => (
                <tr key={t.track_id} onClick={() => setDetailId(t.track_id)}
                  data-testid={`ml-row-${t.track_id}`}
                  className={`border-t border-line/40 cursor-pointer hover:bg-white/5 ${detailId === t.track_id ? "bg-amber-500/5" : ""}`}>
                  <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(t.track_id)} onChange={() => toggleSel(t.track_id)}
                      className="accent-amber-500" data-testid={`ml-check-${t.track_id}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <button onClick={(e) => togglePlay(t, e)} className="relative group" data-testid={`ml-play-${t.track_id}`}>
                      <ArtThumb url={t.art_url} size="w-8 h-8" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-md">
                        {preview.id === t.track_id && preview.state === "loading" ? <Loader2 size={12} className="animate-spin text-white" />
                          : preview.id === t.track_id && preview.state === "playing" ? <Pause size={12} className="text-white" />
                          : <Play size={12} className="text-white" />}
                      </span>
                    </button>
                  </td>
                  <td className="px-2 py-1.5 text-ink-primary max-w-44 truncate">{t.title || "—"}</td>
                  <td className="px-2 py-1.5 text-ink-secondary max-w-36 truncate">{t.artist || "—"}</td>
                  <td className="px-2 py-1.5 text-ink-muted max-w-36 truncate hidden lg:table-cell">{t.album || "—"}</td>
                  <td className="px-2 py-1.5 text-ink-muted">{t.year || "—"}</td>
                  <td className="px-2 py-1.5">
                    {t.genres?.[0]
                      ? <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] text-brand-amber">{t.genres[0]}{t.genres.length > 1 ? ` +${t.genres.length - 1}` : ""}</span>
                      : <span className="text-ink-muted">—</span>}
                  </td>
                  <td className="px-2 py-1.5 text-ink-muted hidden lg:table-cell" title={t.analysis?.bpm_uncertain ? "Estimated — tempo detection was uncertain for this track" : t.bpm_source === "analysis" ? "Estimated from audio analysis" : ""}>
                    {t.bpm ? `${t.bpm_source === "analysis" ? "~" : ""}${t.bpm}${t.analysis?.bpm_uncertain ? " ⚠" : ""}` : "—"}
                  </td>
                  <td className="px-2 py-1.5 hidden lg:table-cell">
                    {t.energy != null
                      ? <span className="inline-block w-14 h-1.5 rounded bg-white/10 align-middle" title={t.energy <= (data.energy_thresholds?.low_max ?? 40) ? "Low energy" : t.energy >= (data.energy_thresholds?.high_min ?? 70) ? "High energy" : "Medium energy"}><span className={`block h-1.5 rounded ${t.energy <= (data.energy_thresholds?.low_max ?? 40) ? "bg-slate-400" : t.energy >= (data.energy_thresholds?.high_min ?? 70) ? "bg-orange-400" : "bg-sky-400"}`} style={{ width: `${Math.max(t.energy, 3)}%` }} /></span>
                      : <span className="text-ink-muted">—</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    <span className="rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[10px] text-sky-300"
                      title={(t.stations || []).map(stationName).join(", ")}>
                      {(t.stations || []).length} station{(t.stations || []).length === 1 ? "" : "s"}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <StatusPill status={STATUS_PILL[t.lib_status] || "published"}>{t.lib_status}</StatusPill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-3">
          <span className="type-timestamp text-ink-muted" data-testid="ml-pagination-info">
            {data.total === 0 ? "0 tracks" : `${(page - 1) * (data.page_size || 25) + 1}–${Math.min(page * (data.page_size || 25), data.total)} of ${data.total.toLocaleString()} tracks`}
          </span>
          <div className="flex items-center gap-1">
            <button disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); load(f, q, sort, p); }}
              className="rounded-md border border-line p-1.5 text-ink-muted hover:text-ink-primary disabled:opacity-30" data-testid="ml-page-prev">
              <ChevronLeft size={14} />
            </button>
            <span className="type-timestamp text-ink-muted px-1">{page}/{pages}</span>
            <button disabled={page >= pages} onClick={() => { const p = page + 1; setPage(p); load(f, q, sort, p); }}
              className="rounded-md border border-line p-1.5 text-ink-muted hover:text-ink-primary disabled:opacity-30" data-testid="ml-page-next">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {detailId && (
        <div className="xl:col-span-4">
          <TrackDetailsPanel trackId={detailId} stations={stations} genres={genres}
            onClose={() => setDetailId("")}
            onSaved={() => { refresh(); onChanged(); }} />
        </div>
      )}
    </div>
  );
};
